import type { StreamInput } from "@pdfking/io"
import { ZipCreate } from "@pdfking/io"
import type { Distance, Pixel, Point, SpatialRectangle } from "@pdfking/math"
import { AddMargins, Center, CreateColorConvert, ToSpatial } from "@pdfking/math"
import { ZERO_PIXEL } from "@pdfking/math/constants"
import type {
  ColorID,
  CompletePage,
  ExtendedColor,
  LayerID,
  LayerMap,
  Matrix,
  PDFPlotFunction,
  PlotGroup,
  PlotMedia,
  PlotPath,
  PlotRect,
  PlotShape,
  PlotText,
  PlottingContext,
} from "@pdfking/renderer-api"
import {
  HorizontalAlign,
  MatrixCompose,
  MatrixIdentity,
  MatrixToPixels,
  MediaEntryMatrix,
  PlotFunction,
  ToPixels,
  VerticalAlign,
} from "@pdfking/renderer-api"
import { ContentType } from "@pdfking/structs"
import type { Sequence } from "@pdfking/tfunc"
import { Constant, FullMethodDispatcher, Items, Map as MapSync } from "@pdfking/tfunc"

import type { IdmlPage } from "../types/IdmlPage"

type StoryData = {
  id: number
  horizAlign: string
  textColor: string
  fontSize: number
  text: string
}

type CurrentContext = {
  stories: StoryData[]
  nextId: () => number
  getColorId: (id: ColorID | undefined) => string
  getLayerId: (id: LayerID) => string
  getMeasures: (r: SpatialRectangle<Distance>) => SpatialRectangle<Pixel>
  getPoints: (position: SpatialRectangle<Pixel>) => [string, string, string, string]
  getTransformMatrix: (position: SpatialRectangle<Pixel>, matrix: Matrix<Pixel>) => string
  // A transform written in the API's y-up space, as IDML's y-down ItemTransform.
  itemTransform: (matrix: Matrix<Pixel>) => string
  getXY: (point: Point<Distance>) => Point<Pixel>
  // The groups this shape sits in, composed, in the API's y-up space. IDML has
  // no group element (and InDesign would force one layer on its children), so
  // every item carries the whole transform in its own ItemTransform.
  matrix: Matrix<Pixel>
  normalize: (value: Distance) => Pixel
  unit_to_px: Pixel
  getSource: (media: PlotMedia) => IdmlPage | undefined
}

// Default layer used when no `layer_map` is supplied — preserves the previous
// single-layer output for callers that don't pass layers.
const DEFAULT_LAYER_SELF = "uc7"

// Bleed added around a placed media's bounding box — 5mm, matching the Python

type LayerResolver = {
  declared: [string, LayerMap[LayerID]][]
  activeLayer: string
  resolveItemLayer: (id: LayerID) => string
}

// Mirrors Python `idml.py`: declare one `<Layer>` per non-merged layer; shapes
// on a merged layer (`idml_merge_with`) reference the parent. Unknown/empty →
// fall back to the first declared layer (or the synthetic default).
const createLayerResolver = (layer_map: LayerMap): LayerResolver => {
  const declared = Items(layer_map).filter(([, l]) => !l.idml_merge_with)

  if (declared.length === 0) {
    return {
      declared: [],
      activeLayer: DEFAULT_LAYER_SELF,
      resolveItemLayer: Constant(DEFAULT_LAYER_SELF),
    }
  }

  const declared_ids = new Set(declared.map(([id]) => id))
  const fallback = declared[0]![0]

  return {
    declared,
    activeLayer: `layer_${fallback}`,
    resolveItemLayer: (id: LayerID) => {
      const layer = layer_map[id]
      const target = layer?.idml_merge_with ?? id
      return declared_ids.has(target) ? `layer_${target}` : `layer_${fallback}`
    },
  }
}

const n: (value: number) => string = String

// Escape XML text content — mark/registration text carries raw `<`, `>`, `&`
// (e.g. `< • TOP • >`), which otherwise breaks the Story XML.
const escapeXml = (value: string): string =>
  value.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")

const serializeMatrix = ([a, b, c, d, e, f]: Matrix<Pixel>): string =>
  `${n(a)} ${n(b)} ${n(c)} ${n(d)} ${n(e)} ${n(f)}`

// IDML's y grows down and the API's grows up, so a transform crosses over as
// `flip · M · flip`: the linear part keeps its sign on x and flips it on y, and
// an angle comes out negated — which is the same rotation, seen the other way.
const toIdmlSpace = (matrix: Matrix<Pixel>, height: Pixel): Matrix<Pixel> => {
  const flip: Matrix<Pixel> = [1, 0, 0, -1, ZERO_PIXEL, height]
  return MatrixCompose(MatrixCompose(flip, matrix), flip)
}

const serializeColor = CreateColorConvert(
  rgb => `${n(rgb.red * 100)} ${n(rgb.green * 100)} ${n(rgb.blue * 100)}`,
  cmyk =>
    `${n(cmyk.cyan * 100)} ${n(cmyk.magenta * 100)} ${n(cmyk.yellow * 100)} ${n(cmyk.black * 100)}`
)

const serializeColorSpace = CreateColorConvert(Constant("RGB"), Constant("CMYK"))

// A color connected to one of InDesign's built-in defaults resolves to the
// reserved self id directly — never redeclared as a custom swatch.
const color_idml_id = (id: string, color: ExtendedColor): string =>
  color.idml_reserved ? `Color/${color.idml_reserved}` : `PdfEngine/${id}`

// `ColorOverride` value InDesign expects for each reserved built-in (IDML
// spec Appendix D `ColorOverride_EnumValue` + the `Color/Black` snippet
// example in Appendix B) — declaring these explicitly, rather than omitting
// them, is what makes InDesign resolve Cyan/Magenta/Yellow reliably.
const RESERVED_COLOR_OVERRIDE: Record<string, string> = {
  Paper: "Specialpaper",
  Black: "Specialblack",
  Registration: "Specialregistration",
}

function* plot_rect(shape: PlotRect, context: CurrentContext): Generator<string> {
  const id = context.nextId()
  const position = context.getMeasures(shape.position)
  const points = context.getPoints(position)
  const matrix = context.getTransformMatrix(position, context.matrix)

  const strokeColor = context.getColorId(shape.stroke_color)
  const fillColor = context.getColorId(shape.fill_color)

  yield `<Rectangle Self="shape_${id}" FillTint="100" StrokeTint="100" ItemLayer="${context.getLayerId(shape.layer)}" StrokeColor="${strokeColor}" FillColor="${fillColor}" StrokeWeight="${n(context.normalize(shape.stroke_width))}" ItemTransform="${matrix}">`
  yield "<Properties>"
  yield "<PathGeometry>"
  yield `<GeometryPathType PathOpen="false">`
  yield "<PathPointArray>"
  for (const point of points) {
    yield `<PathPointType Anchor="${point}" LeftDirection="${point}" RightDirection="${point}" />`
  }
  yield "</PathPointArray>"
  yield "</GeometryPathType>"
  yield "</PathGeometry>"
  yield "</Properties>"
  yield "</Rectangle>"
}

function* plot_text(shape: PlotText, context: CurrentContext): Generator<string> {
  const id = context.nextId()
  const position = context.getMeasures(shape.position)

  // The frame is the box as written: text carries no rotation of its own any
  // more, so InDesign's own Top/Bottom/Left/RightAlign lands where every other
  // backend puts it. A turned label is a `plot_group` around this shape, and
  // the group's transform is already in `context.matrix`.
  const points = context.getPoints(position)
  const matrix = context.getTransformMatrix(position, context.matrix)

  const strokeColor = context.getColorId(shape.stroke_color)
  const fillColor = context.getColorId(shape.fill_color)
  const textColor = context.getColorId(shape.text_color)

  const vertAlign =
    shape.vertical_align === VerticalAlign.TOP
      ? "TopAlign"
      : shape.vertical_align === VerticalAlign.BOTTOM
        ? "BottomAlign"
        : "CenterAlign"

  const horizAlign =
    shape.horizontal_align === HorizontalAlign.LEFT
      ? "LeftAlign"
      : shape.horizontal_align === HorizontalAlign.RIGHT
        ? "RightAlign"
        : "CenterAlign"

  yield `<TextFrame Self="shape_${id}" ParentStory="story_${id}" FillTint="100" StrokeTint="100" ItemLayer="${context.getLayerId(shape.layer)}" StrokeColor="${strokeColor}" FillColor="${fillColor}" StrokeWeight="${n(context.normalize(shape.stroke_width))}" ItemTransform="${matrix}">`
  yield "<Properties>"
  yield "<PathGeometry>"
  yield `<GeometryPathType PathOpen="false">`
  yield "<PathPointArray>"
  for (const point of points) {
    yield `<PathPointType Anchor="${point}" LeftDirection="${point}" RightDirection="${point}" />`
  }
  yield "</PathPointArray>"
  yield "</GeometryPathType>"
  yield "</PathGeometry>"
  yield "</Properties>"
  yield `<TextFramePreference VerticalJustification="${vertAlign}" />`
  yield "</TextFrame>"

  // Store story data in context for later generation
  context.stories.push({
    fontSize: shape.font_size || 12,
    horizAlign,
    id,
    text: shape.text,
    textColor,
  })
}

function* plot_path(shape: PlotPath, context: CurrentContext): Generator<string> {
  const id = context.nextId()

  const strokeColor = context.getColorId(shape.stroke_color)
  const fillColor = shape.closed ? context.getColorId(shape.fill_color) : "Swatch/None"

  const pathOpen = shape.closed ? "false" : "true"

  yield `<GraphicLine Self="shape_${id}" FillTint="100" StrokeTint="100" ItemLayer="${context.getLayerId(shape.layer)}" StrokeColor="${strokeColor}" FillColor="${fillColor}" StrokeWeight="${n(context.normalize(shape.stroke_width))}" ItemTransform="${context.itemTransform(context.matrix)}">`
  yield "<Properties>"
  yield "<PathGeometry>"
  yield `<GeometryPathType PathOpen="${pathOpen}">`
  yield "<PathPointArray>"
  const at = (pt: Point<Distance>) => {
    const { x, y } = context.getXY(pt)
    return `${n(x)} ${n(y)}`
  }
  for (const pt of shape.points) {
    yield `<PathPointType Anchor="${at(pt)}" LeftDirection="${at(pt.before)}" RightDirection="${at(pt.after)}" />`
  }
  yield "</PathPointArray>"
  yield "</GeometryPathType>"
  yield "</PathGeometry>"
  yield "</Properties>"
  yield "</GraphicLine>"
}

// The stand-in a slot links when it has no source page to place — an empty
// slot, or a row whose upload is not in the job — at the slot's own page.
const FALLBACK_PATH = "file.pdf"

// Place a linked PDF for a media shape — mirrors Python `idml.py` `plot_media`:
// an outer crop `<Rectangle>` containing an inner (graphic-sized, transformed)
// `<Rectangle>` that holds a `<PDF>` with a `<Link LinkResourceURI>` and a
// `<PDFAttribute PageNumber>`. A slot whose upload is in `file_map` links that
// file at its own page; any other links `file.pdf`.
function* plot_media(shape: PlotMedia, context: CurrentContext): Generator<string> {
  const source = context.getSource(shape)

  const id = context.nextId()
  const { normalize } = context
  // The mask stays with the box: the crop frame is the box grown by it, and
  // the media's own transform moves the page inside that frame.
  const crop_position = AddMargins(shape.position, shape.mask)

  // The graphic, and where its frame's origin sits in it: the point that lands
  // on the slot's centre.
  //
  // A source page: its media box — the trim grown by its own bleed — is the
  // graphic, and its trim centre is that point, the same rule pdfium's media
  // drawing and the preview follow. The graphic's own space is y-down from the
  // media box's top-left corner.
  //
  // The stand-in: the slot grown by the bleed the media shows — the pdf is
  // assumed made for this slot, so its trim lands on the slot's centre.
  const { trim, bleed } = source ? source.media : { trim: shape.position, bleed: shape.mask }
  const graphic_size = AddMargins(ToSpatial(trim), bleed)
  const trim_centre = Center(ToSpatial(trim, { x: bleed.left, y: bleed.top }))
  const graphic = {
    width: normalize(graphic_size.width),
    height: normalize(graphic_size.height),
    cx: normalize(trim_centre.x),
    cy: normalize(trim_centre.y),
  }
  const graphic_matrix = serializeMatrix([1, 0, 0, 1, -graphic.cx as Pixel, -graphic.cy as Pixel])
  const media_path = `file:${source ? source.path : FALLBACK_PATH}`

  // The inner frame holds the whole graphic, its origin on the slot's centre,
  // and carries the file's matrix and the media's own. It sits in the crop frame, whose
  // origin is the crop's centre: a move in IDML's y-down space is the same
  // move with y the other way in the API's, where the transform is written.
  const media_box = context.getMeasures(shape.position)
  const crop = context.getMeasures(crop_position)
  const crop_centre = Center(crop)
  const inner_matrix = context.getTransformMatrix(
    media_box,
    MatrixCompose(MatrixToPixels(context, MediaEntryMatrix(source, shape)), [
      1,
      0,
      0,
      1,
      -crop_centre.x as Pixel,
      crop_centre.y,
    ])
  )

  const layer = context.getLayerId(shape.layer)
  const outer_points = context.getPoints(crop)
  const left = -graphic.cx
  const top = -graphic.cy
  const right = graphic.width - graphic.cx
  const bottom = graphic.height - graphic.cy
  const inner_points = [
    `${n(left)} ${n(top)}`,
    `${n(left)} ${n(bottom)}`,
    `${n(right)} ${n(bottom)}`,
    `${n(right)} ${n(top)}`,
  ]

  yield `<Rectangle Self="shape_${id}" FillTint="100" StrokeTint="100" ItemLayer="${layer}" StrokeColor="Swatch/None" FillColor="Swatch/None" StrokeWeight="0" ItemTransform="${context.getTransformMatrix(crop, context.matrix)}">`
  yield '<Properties><PathGeometry><GeometryPathType PathOpen="false"><PathPointArray>'
  for (const point of outer_points) {
    yield `<PathPointType Anchor="${point}" LeftDirection="${point}" RightDirection="${point}" />`
  }
  yield "</PathPointArray></GeometryPathType></PathGeometry></Properties>"

  yield `<Rectangle Self="outer_${id}" FillTint="100" StrokeTint="100" ItemLayer="${layer}" StrokeColor="Swatch/None" FillColor="Swatch/None" StrokeWeight="0" ItemTransform="${inner_matrix}">`
  yield '<Properties><PathGeometry><GeometryPathType PathOpen="false"><PathPointArray>'
  for (const point of inner_points) {
    yield `<PathPointType Anchor="${point}" LeftDirection="${point}" RightDirection="${point}" />`
  }
  yield "</PathPointArray></GeometryPathType></PathGeometry></Properties>"

  yield `<PDF Self="image_${id}" ItemTransform="${graphic_matrix}">`
  // GraphicBounds pins the graphic's own-space extent (= the linked page's
  // media box). Without it, importers must guess: Affinity fits the PDF's ink
  // bbox to the frame, blowing the content up past its slot. With explicit
  // bounds, ItemTransform maps [0,0,w,h] onto the frame deterministically.
  yield `<Properties><GraphicBounds Left="0" Top="0" Right="${n(graphic.width)}" Bottom="${n(graphic.height)}" /></Properties>`
  yield `<Link Self="link_${id}" LinkResourceURI="${media_path}" />`
  yield `<ClippingPathSettings UseHighResolutionImage="true" />`
  yield `<PDFAttribute PageNumber="${n(source ? source.page : shape.page_number)}" PDFCrop="CropMedia" TransparentBackground="true" />`
  yield "</PDF>"
  yield "</Rectangle>"
  yield "</Rectangle>"
}

// InDesign keeps a group's items on one layer and the plot's groups may span
// layers, so groups are flattened: each child's ItemTransform carries the
// composed matrix and the items come out loose, on their own layers.
function* plot_group(shape: PlotGroup, context: CurrentContext): Generator<string> {
  // The group's transform is written in the plot's own units; everything it is
  // composed with here — the page flip, an item's centre — was measured in
  // pixels, so it converts on the way in.
  const matrix = MatrixCompose(MatrixToPixels(context, shape.matrix), context.matrix)
  for (const child of shape.shapes) {
    yield* dispatch(child, { ...context, matrix })
  }
}

const dispatch: (shape: PlotShape, context: CurrentContext) => Generator<string> =
  FullMethodDispatcher<[PlotShape, CurrentContext], Generator<string>>({
    [PlotFunction.PLOT_GROUP]: plot_group,
    [PlotFunction.PLOT_RECT]: plot_rect,
    [PlotFunction.PLOT_TEXT]: plot_text,
    [PlotFunction.PLOT_PATH]: plot_path,
    [PlotFunction.PLOT_MEDIA]: plot_media,
  })

function* generateSpreadWithStories(
  page: CompletePage,
  context: PlottingContext<IdmlPage>,
  spreadSelf: string,
  pageSelf: string,
  nextId: () => number,
  stories: StoryData[]
): Generator<string> {
  const width = ToPixels(context, page.width)
  const height = ToPixels(context, page.height)

  const normalize = (value: Distance): Pixel => ToPixels(context, value)

  // Positions are trim-relative (origin = the page's top-left, matching the
  // `<Page GeometricBounds="0 0 height width">` below), same as Python
  // `idml.py` `normalize_x/y`. IDML's page system is already Y-down like the
  // imposition's `Distance` Y, so no flip. Do NOT add `page.bleed` here — the
  // bleed is a document setting (`DocumentBleedTopOffset`), not a per-shape
  // offset; adding it shifts every shape (and guide) into the page by the bleed.
  // The API's origin is the trim box's bottom-left with y up; IDML's page
  // system is y-down from the top-left (matching `<Page GeometricBounds="0 0
  // height width">`), so y flips here. Do NOT add `page.bleed` — the bleed is a
  // document setting (`DocumentBleedTopOffset`), not a per-shape offset.
  const getXY = (point: Point<Distance>): Point<Pixel> => ({
    x: normalize(point.x),
    y: (height - normalize(point.y)) as Pixel,
  })

  const getMeasures = (r: SpatialRectangle<Distance>): SpatialRectangle<Pixel> => {
    const { x } = getXY(r)
    const w = normalize(r.width)
    const h = normalize(r.height)
    // The box's bottom edge in API space is its top edge here.
    return {
      height: h,
      width: w,
      x,
      y: (height - normalize(r.y) - h) as Pixel,
    }
  }

  const getPoints = (position: SpatialRectangle<Pixel>): [string, string, string, string] => {
    const { width, height } = position
    const hw = width / 2
    const hh = height / 2
    return [`${n(-hw)} ${n(-hh)}`, `${n(-hw)} ${n(hh)}`, `${n(hw)} ${n(hh)}`, `${n(hw)} ${n(-hh)}`]
  }

  // An item is drawn centred on its own origin, so its transform is "move to
  // the centre", then whatever its groups do — crossed over into IDML's space.
  const itemTransform = (matrix: Matrix<Pixel>): string =>
    serializeMatrix(toIdmlSpace(matrix, height))

  const getTransformMatrix = (position: SpatialRectangle<Pixel>, matrix: Matrix<Pixel>): string => {
    const { x, y } = Center(position)
    return serializeMatrix(MatrixCompose([1, 0, 0, 1, x, y], toIdmlSpace(matrix, height)))
  }

  const getColorId = (id: ColorID | undefined): string => {
    const color = id ? context.color_map[id] : undefined
    return color ? color_idml_id(id!, color) : "Swatch/None"
  }

  const { resolveItemLayer } = createLayerResolver(context.layer_map)

  const getSource = ({ filename, page_number }: PlotMedia): IdmlPage | undefined =>
    context.file_map[filename]?.[page_number]

  const currentContext: CurrentContext = {
    itemTransform,
    matrix: MatrixIdentity<Pixel>(),
    unit_to_px: context.unit_to_px,
    getColorId,
    getLayerId: resolveItemLayer,
    getMeasures,
    getSource,
    getPoints,
    getTransformMatrix,
    getXY,
    nextId,
    normalize,
    stories,
  }

  yield '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>\n'
  yield '<idPkg:Spread xmlns:idPkg="http://ns.adobe.com/AdobeInDesign/idml/1.0/packaging" DOMVersion="17.0">'
  yield `<Spread Self="${spreadSelf}" PageCount="1">`
  yield `<Page Self="${pageSelf}" GeometricBounds="0 0 ${n(height)} ${n(width)}" ItemTransform="1 0 0 1 0 0"><MarginPreference ColumnCount="1" Top="0" Bottom="0" Left="0" Right="0" /></Page>`

  for (const shape of page.shapes) {
    yield* dispatch(shape, currentContext)
  }

  yield "</Spread>"
  yield "</idPkg:Spread>"
}

function* generateIDMLWithStories(
  pages: CompletePage[],
  context: PlottingContext<IdmlPage>,
  stories: StoryData[],
  spreadFiles: string[]
): Generator<string> {
  const normalize = (value: Distance): Pixel => ToPixels(context, value)

  // The document bleed is the pages' own, like the page size: one per document.
  const { bleed } = pages[0]!
  const storyList = stories.map(s => `story_${s.id}`).join(" ")
  const { declared, activeLayer } = createLayerResolver(context.layer_map)

  yield '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>\n'
  yield '<?aid style="50" type="document" readerVersion="6.0" featureSet="257" product="17.2(105)" ?>\n'
  yield `<Document xmlns:idPkg="http://ns.adobe.com/AdobeInDesign/idml/1.0/packaging" DOMVersion="17.0" Self="d" StoryList="${storyList} ub0" Name="" ZeroPoint="0 0" ActiveLayer="${activeLayer}" CMYKProfile="$ID/" RGBProfile="$ID/" SolidColorIntent="UseColorSettings" AfterBlendingIntent="UseColorSettings" DefaultImageIntent="UseColorSettings" RGBPolicy="ColorPolicyOff" CMYKPolicy="ColorPolicyOff" AccurateLABSpots="false">`

  // Language definitions (required by Affinity)
  yield '<Language Self="Language/$ID/English%3a USA" Name="$ID/English: USA" SingleQuotes="&apos;&apos;" DoubleQuotes="&quot;&quot;" PrimaryLanguageName="$ID/English" SublanguageName="$ID/USA" Id="269" HyphenationVendor="Hunspell" SpellingVendor="Hunspell" />'

  yield `<DocumentPreference Self="doc_pref" FacingPages="false" DocumentBleedUniformSize="false" DocumentBleedTopOffset="${n(normalize(bleed.top))}" DocumentBleedBottomOffset="${n(normalize(bleed.bottom))}" DocumentBleedInsideOrLeftOffset="${n(normalize(bleed.left))}" DocumentBleedOutsideOrRightOffset="${n(normalize(bleed.right))}" PageHeight="${n(normalize(pages[0]!.height))}" PageWidth="${n(normalize(pages[0]!.width))}" />`

  // One `<Layer>` per non-merged layer (matches Python idml renderer). Empty
  // layer_map → a single default layer so minimal callers still get valid IDML.
  if (declared.length === 0) {
    yield `<Layer Self="${DEFAULT_LAYER_SELF}" Name="Layer 1" Locked="false" Printable="true"><Properties><LayerColor type="enumeration">LightBlue</LayerColor></Properties></Layer>`
  } else {
    for (const [id, layer] of declared) {
      yield `<Layer Self="layer_${id}" Name="${layer.name ?? id}" Locked="${layer.locked ? "true" : "false"}" Printable="${layer.printable ? "true" : "false"}"><Properties><LayerColor type="enumeration">${layer.idml_color ?? ""}</LayerColor></Properties></Layer>`
    }
  }

  // Reference external files instead of inline content
  yield '<idPkg:Graphic src="Resources/Graphic.xml" />'
  for (const file of spreadFiles) {
    yield `<idPkg:Spread src="${file}" />`
  }

  // Add story references
  for (const story of stories) {
    yield `<idPkg:Story src="Stories/Story_story_${story.id}.xml" />`
  }

  yield "</Document>"
}

function* generateGraphicXml(colorMap: Record<string, ExtendedColor>): Generator<string> {
  yield '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>\n'
  yield '<idPkg:Graphic xmlns:idPkg="http://ns.adobe.com/AdobeInDesign/idml/1.0/packaging" DOMVersion="17.0">'

  // A color with `idml_reserved` resolves to InDesign's built-in Color/<Name>
  // (via `colorSelfId`), so it's declared here under that reserved self id
  // with the matching `ColorOverride`, not as a custom swatch — relying on
  // InDesign to silently conjure Cyan/Magenta/Yellow when they're left
  // undeclared isn't reliable in practice.
  for (const [id, color] of Items(colorMap)) {
    const reserved = color.idml_reserved
    const colorOverride = reserved ? RESERVED_COLOR_OVERRIDE[reserved] : "Normal"
    const editable = reserved ? "false" : "true"
    yield `<Color Self="${color_idml_id(id, color)}" Name="${reserved ?? id}" ColorValue="${serializeColor(color)}" ColorEditable="${editable}" ColorRemovable="${editable}" Visible="true" Model="${color.idml_model ?? "Process"}" Space="${serializeColorSpace(color)}" ColorOverride="${colorOverride}" />`
  }

  yield '<Swatch Self="Swatch/None" Name="None" ColorEditable="false" ColorRemovable="false" Visible="true" SwatchCreatorID="7937" />'
  yield "</idPkg:Graphic>"
}

function* generateStory(story: StoryData): Generator<string> {
  yield '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>\n'
  yield '<idPkg:Story xmlns:idPkg="http://ns.adobe.com/AdobeInDesign/idml/1.0/packaging" DOMVersion="17.0">'
  yield `<Story Self="story_${story.id}" UserText="true" IsEndnoteStory="false" AppliedTOCStyle="n" TrackChanges="false" StoryTitle="$ID/" AppliedNamedGrid="n">`
  yield '<StoryPreference OpticalMarginAlignment="false" OpticalMarginSize="12" FrameType="TextFrameType" StoryOrientation="Horizontal" StoryDirection="LeftToRightDirection" />'
  yield '<InCopyExportOption IncludeGraphicProxies="true" IncludeAllResources="false" />'
  yield '<ParagraphStyleRange AppliedParagraphStyle="ParagraphStyle/$ID/NormalParagraphStyle">'
  yield `<CharacterStyleRange AppliedCharacterStyle="CharacterStyle/$ID/[No character style]" Justification="${story.horizAlign}" FillTint="100" FillColor="${story.textColor}" PointSize="${story.fontSize}">`
  yield '<Properties><AppliedFont type="string">Helvetica</AppliedFont></Properties>'
  yield `<Content>${escapeXml(story.text)}</Content>`
  yield "</CharacterStyleRange>"
  yield "</ParagraphStyleRange>"
  yield "</Story>"
  yield "</idPkg:Story>"
}

// the zip's own chunks, one IDML for all the pages
export const IDMLPlot: PDFPlotFunction<IdmlPage, StreamInput> = (pages, context) => {
  const stories: StoryData[] = []
  // One counter shared across every spread/page/shape/story Self — mirrors
  // Python's single `self._counter`, guaranteeing document-wide unique IDs.
  let counter = 0
  const nextId = () => ++counter

  // One byte chunk per file. Collapsing the thousands of tiny per-tag string
  // yields into a single `Uint8Array` per file is critical: otherwise a big job
  // emits 100k+ chunks, each of which becomes a real `Blob` downstream —
  // `GenerateFile` (`blob.arrayBuffer()` per chunk) and the worker→main
  // structured-clone then take ~100s each. One chunk per file keeps it linear.
  const fileChunk = (parts: Sequence<string>): string => [...parts].join("")

  // Eagerly build each spread's chunk (forces `plot_text` to push into
  // `stories`) before `generateIDMLWithStories` reads the final story list.
  const spreads = pages.map(page => {
    const spreadSelf = `spread_${nextId()}`
    const pageSelf = `page_${nextId()}`
    return {
      stream: fileChunk(
        generateSpreadWithStories(page, context, spreadSelf, pageSelf, nextId, stories)
      ),
      name: `Spreads/Spread_${spreadSelf}.xml`,
    }
  })

  // The zip's own chunks, as they come: whoever stores them decides what they
  // become, so nothing is wrapped on the way out.
  return ZipCreate([
    {
      stream: [ContentType.IDML],
      name: "mimetype",
    },
    {
      stream: fileChunk(generateGraphicXml(context.color_map)),
      name: "Resources/Graphic.xml",
    },
    ...spreads,
    {
      stream: fileChunk(
        generateIDMLWithStories(
          pages,
          context,
          stories,
          spreads.map(s => s.name)
        )
      ),
      name: "designmap.xml",
    },
    ...MapSync(
      story => ({
        stream: fileChunk(generateStory(story)),
        name: `Stories/Story_story_${story.id}.xml`,
      }),
      stories
    ),
  ])
}
